import { functionDoc } from '../functionDoc';
import { ROLES, SHOTS } from '../docHelpers';

/** Documentación detallada (plantilla 12 apartados) — Maquinarias. */
export const maquinariaSections = [
  {
    id: "maq-maquinas",
    name: "Máquinas",
    type: "form",
    route: "/machinery/machines",
    locationPath: "Maquinarias → Configuración → Máquinas",
    generalDescription: "Maestro de máquinas fijas o equipos sin placa vehicular: código interno, categoría, horómetro base y estado operativo. Alimenta la bitácora de eventos y las alertas de mantenimiento preventivo.",
    prerequisites: [
      "Módulo Maquinarias habilitado en Administrador → Habilitación de módulos.",
      "Usuario administrador o limitado con acceso al módulo maquinaria."
    ],
    interfaceElements: [
      {
        name: "Código / nombre",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Identificador visible en eventos y reportes.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Categoría",
        controlType: "Select",
        required: true,
        editable: true,
        automatic: false,
        description: "Clasificación configurada en Categorías.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Horómetro / estado",
        controlType: "Número / select",
        required: false,
        editable: true,
        automatic: false,
        description: "Referencia para mantenimiento por horas.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Maquinarias → Configuración → Máquinas.",
      "Pulse Nuevo o edite una fila.",
      "Complete categoría y datos técnicos.",
      "Guarde y verifique en Eventos."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Máquinas"
      },
      steps: [
        "Maquinarias → Configuración → Máquinas.",
        "Pulse Nuevo o edite una fila.",
        "Complete categoría y datos técnicos.",
        "Guarde y verifique en Eventos."
      ]
    },
    validations: [
      "Código o placa únicos según tipo de equipo.",
      "Categoría y estado coherentes con reportes."
    ],
    expectedResult: [
      "Registro visible en bitácora, mantenimiento y reportes."
    ],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "maq-categorias",
      "maq-eventos"
    ],
    observations: [
      "Operaciones de taller, logística o mina con equipo propio registrado."
    ],
    screenshots: [
      SHOTS.listado,
      SHOTS.formularioCompleto
    ],
    roles: "Operaciones de taller, logística o mina con equipo propio registrado."
  },

  {
    id: "maq-vehiculos",
    name: "Vehículos",
    type: "form",
    route: "/machinery/vehicles",
    locationPath: "Maquinarias → Configuración → Vehículos",
    generalDescription: "Registro de unidades móviles con placa, categoría y kilometraje. Los eventos de viaje y consumo se asocian aquí; los conductores se seleccionan al registrar eventos.",
    prerequisites: [
      "Módulo Maquinarias habilitado en Administrador → Habilitación de módulos.",
      "Usuario administrador o limitado con acceso al módulo maquinaria."
    ],
    interfaceElements: [
      {
        name: "Placa",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Identificación legal del vehículo.",
        behavior: "Revise el comportamiento al interactuar en pantalla.",
        example: "ABC-123"
      },
      {
        name: "Categoría",
        controlType: "Select",
        required: true,
        editable: true,
        automatic: false,
        description: "Tipo de unidad (camión, pickup, etc.).",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Kilometraje actual",
        controlType: "Número",
        required: false,
        editable: true,
        automatic: false,
        description: "Base para alertas de mantenimiento por km.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Entre a Vehículos.",
      "Cree o edite la unidad.",
      "Asigne categoría y km si aplica.",
      "Guarde."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Vehículos"
      },
      steps: [
        "Entre a Vehículos.",
        "Cree o edite la unidad.",
        "Asigne categoría y km si aplica.",
        "Guarde."
      ]
    },
    validations: [
      "Código o placa únicos según tipo de equipo.",
      "Categoría y estado coherentes con reportes."
    ],
    expectedResult: [
      "Registro visible en bitácora, mantenimiento y reportes."
    ],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "maq-conductores",
      "maq-eventos"
    ],
    observations: [
      "Operaciones de taller, logística o mina con equipo propio registrado."
    ],
    screenshots: [
      SHOTS.listado,
      SHOTS.formularioCompleto
    ],
    roles: "Operaciones de taller, logística o mina con equipo propio registrado."
  },

  {
    id: "maq-categorias",
    name: "Categorías de equipo",
    type: "form",
    route: "/machinery/categories",
    locationPath: "Maquinarias → Configuración → Categorías",
    generalDescription: "Catálogo de tipos de máquina o vehículo para agrupar reportes y reglas de mantenimiento. Debe existir antes de dar de alta equipos.",
    prerequisites: [
      "Módulo Maquinarias habilitado en Administrador → Habilitación de módulos.",
      "Usuario administrador o limitado con acceso al módulo maquinaria."
    ],
    interfaceElements: [
      {
        name: "Nombre de categoría",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Etiqueta en listados.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Descripción",
        controlType: "Área de texto",
        required: false,
        editable: true,
        automatic: false,
        description: "Detalle opcional para operadores.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Abra Categorías.",
      "Cree categorías necesarias para su flota.",
      "Asigne cada equipo a una categoría."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Categorías de equipo"
      },
      steps: [
        "Abra Categorías.",
        "Cree categorías necesarias para su flota.",
        "Asigne cada equipo a una categoría."
      ]
    },
    validations: [
      "Código o placa únicos según tipo de equipo.",
      "Categoría y estado coherentes con reportes."
    ],
    expectedResult: [
      "Registro visible en bitácora, mantenimiento y reportes."
    ],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "maq-maquinas",
      "maq-vehiculos"
    ],
    observations: [
      "Operaciones de taller, logística o mina con equipo propio registrado."
    ],
    screenshots: [
      SHOTS.listado,
      SHOTS.formularioCompleto
    ],
    roles: "Operaciones de taller, logística o mina con equipo propio registrado."
  },

  {
    id: "maq-conductores",
    name: "Conductores",
    type: "form",
    route: "/machinery/conductors",
    locationPath: "Maquinarias → Configuración → Conductores",
    generalDescription: "Personas autorizadas a operar vehículos. Se eligen al registrar eventos de conducción o viaje en la bitácora.",
    prerequisites: [
      "Módulo Maquinarias habilitado en Administrador → Habilitación de módulos.",
      "Usuario administrador o limitado con acceso al módulo maquinaria."
    ],
    interfaceElements: [
      {
        name: "Nombre completo",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Identificación del conductor.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Documento / licencia",
        controlType: "Campo",
        required: false,
        editable: true,
        automatic: false,
        description: "Referencia interna o número de brevete.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Estado activo",
        controlType: "Checkbox",
        required: false,
        editable: true,
        automatic: false,
        description: "Conductores inactivos no aparecen en selectores.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Registre conductores antes de eventos con vehículo.",
      "Mantenga activos solo al personal vigente.",
      "Use en Eventos al registrar viajes."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Conductores"
      },
      steps: [
        "Registre conductores antes de eventos con vehículo.",
        "Mantenga activos solo al personal vigente.",
        "Use en Eventos al registrar viajes."
      ]
    },
    validations: [
      "Código o placa únicos según tipo de equipo.",
      "Categoría y estado coherentes con reportes."
    ],
    expectedResult: [
      "Registro visible en bitácora, mantenimiento y reportes."
    ],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "maq-vehiculos",
      "maq-eventos"
    ],
    observations: [
      "Operaciones de taller, logística o mina con equipo propio registrado."
    ],
    screenshots: [
      SHOTS.listado,
      SHOTS.formularioCompleto
    ],
    roles: "Operaciones de taller, logística o mina con equipo propio registrado."
  },

  {
    id: "maq-tipos-evento",
    name: "Tipos de evento",
    type: "form",
    route: "/machinery/events/config",
    locationPath: "Maquinarias → Configuración → Tipos de evento",
    generalDescription: "Define qué puede registrarse en la bitácora: horas de uso, fallas, viajes, combustible, etc. Cada tipo puede exigir campos distintos (horómetro, km, conductor).",
    prerequisites: [
      "Módulo Maquinarias habilitado en Administrador → Habilitación de módulos.",
      "Usuario administrador o limitado con acceso al módulo maquinaria."
    ],
    interfaceElements: [
      {
        name: "Nombre del tipo",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Aparece al crear un evento.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Aplica a",
        controlType: "Select",
        required: false,
        editable: true,
        automatic: false,
        description: "Máquina, vehículo o ambos.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Campos requeridos",
        controlType: "Configuración",
        required: false,
        editable: true,
        automatic: false,
        description: "Horas, km, conductor según diseño.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Configure tipos antes de operar la bitácora.",
      "Alinee nombres con procedimientos de taller.",
      "Pruebe creando un evento de prueba."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Tipos de evento"
      },
      steps: [
        "Configure tipos antes de operar la bitácora.",
        "Alinee nombres con procedimientos de taller.",
        "Pruebe creando un evento de prueba."
      ]
    },
    validations: [
      "Código o placa únicos según tipo de equipo.",
      "Categoría y estado coherentes con reportes."
    ],
    expectedResult: [
      "Registro visible en bitácora, mantenimiento y reportes."
    ],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "maq-eventos"
    ],
    observations: [
      "Operaciones de taller, logística o mina con equipo propio registrado."
    ],
    screenshots: [
      SHOTS.listado,
      SHOTS.formularioCompleto
    ],
    roles: "Operaciones de taller, logística o mina con equipo propio registrado."
  },

  {
    id: "maq-eventos",
    name: "Eventos (bitácora)",
    type: "form",
    route: "/machinery/events",
    locationPath: "Maquinarias → Eventos (Bitácora)",
    generalDescription: "Registro cronológico de uso, fallas, viajes y otros hechos por equipo. Actualiza horómetro/km y puede disparar alertas de mantenimiento según reglas del sistema.",
    prerequisites: [
      "Módulo Maquinarias habilitado en Administrador → Habilitación de módulos.",
      "Usuario administrador o limitado con acceso al módulo maquinaria."
    ],
    interfaceElements: [
      {
        name: "Equipo",
        controlType: "Select",
        required: true,
        editable: true,
        automatic: false,
        description: "Máquina o vehículo.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Tipo de evento",
        controlType: "Select",
        required: true,
        editable: true,
        automatic: false,
        description: "Según catálogo configurado.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Fecha y mediciones",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Horas, km, detalle y conductor si aplica.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Maquinarias → Eventos (Bitácora).",
      "Seleccione equipo y tipo de evento.",
      "Ingrese fecha, horómetro/km y observaciones.",
      "Guarde; revise alertas de mantenimiento si el evento lo disparó."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Eventos (bitácora)"
      },
      steps: [
        "Maquinarias → Eventos (Bitácora).",
        "Seleccione equipo y tipo de evento.",
        "Ingrese fecha, horómetro/km y observaciones.",
        "Guarde; revise alertas de mantenimiento si el evento lo disparó."
      ]
    },
    validations: [
      "Código o placa únicos según tipo de equipo.",
      "Categoría y estado coherentes con reportes."
    ],
    expectedResult: [
      "Registro visible en bitácora, mantenimiento y reportes."
    ],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "maq-tipos-evento",
      "maq-mantenimiento-alertas"
    ],
    observations: [
      "Operaciones de taller, logística o mina con equipo propio registrado."
    ],
    screenshots: [
      SHOTS.listado,
      SHOTS.formularioCompleto
    ],
    roles: "Operaciones de taller, logística o mina con equipo propio registrado."
  },

  {
    id: "maq-mantenimiento-alertas",
    name: "Alertas de mantenimiento",
    type: "screen",
    route: "/machinery/maintenance",
    locationPath: "Maquinarias → Alertas de mantenimiento",
    generalDescription: "Lista de mantenimientos preventivos o correctivos pendientes según horas, km o calendario. Permite cerrar alertas al registrar el trabajo en historial.",
    prerequisites: [
      "Módulo Maquinarias habilitado en Administrador → Habilitación de módulos.",
      "Usuario administrador o limitado con acceso al módulo maquinaria."
    ],
    interfaceElements: [
      {
        name: "Filtro por equipo",
        controlType: "Select",
        required: false,
        editable: true,
        automatic: false,
        description: "Acota alertas abiertas.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Lista de alertas",
        controlType: "Tabla",
        required: false,
        editable: true,
        automatic: true,
        description: "Prioridad, vencimiento y tipo.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Registrar atención",
        controlType: "Botón",
        required: false,
        editable: true,
        automatic: false,
        description: "Enlace al historial o cierre.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Revise alertas diariamente.",
      "Programe taller para ítems vencidos.",
      "Al completar, registre en Historial de mantenimientos."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Alertas de mantenimiento"
      },
      steps: [
        "Revise alertas diariamente.",
        "Programe taller para ítems vencidos.",
        "Al completar, registre en Historial de mantenimientos."
      ]
    },
    validations: [
      "Código o placa únicos según tipo de equipo.",
      "Categoría y estado coherentes con reportes."
    ],
    expectedResult: [
      "Registro visible en bitácora, mantenimiento y reportes."
    ],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "maq-mantenimiento-historial"
    ],
    observations: [
      "Operaciones de taller, logística o mina con equipo propio registrado."
    ],
    screenshots: [
      SHOTS.listado,
      SHOTS.formularioCompleto
    ],
    roles: "Operaciones de taller, logística o mina con equipo propio registrado."
  },

  {
    id: "maq-mantenimiento-historial",
    name: "Historial de mantenimientos",
    type: "form",
    route: "/machinery/maintenance/history",
    locationPath: "Maquinarias → Historial de mantenimientos",
    generalDescription: "Bitácora de trabajos realizados: fecha, equipo, tipo de servicio, costo y observaciones. Cierra alertas y deja trazabilidad para auditoría operativa.",
    prerequisites: [
      "Módulo Maquinarias habilitado en Administrador → Habilitación de módulos.",
      "Usuario administrador o limitado con acceso al módulo maquinaria."
    ],
    interfaceElements: [
      {
        name: "Equipo y fecha",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Qué se atendió y cuándo.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Descripción del trabajo",
        controlType: "Área de texto",
        required: false,
        editable: true,
        automatic: false,
        description: "Repuestos, horas de taller, proveedor.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Costo",
        controlType: "Número",
        required: false,
        editable: true,
        automatic: false,
        description: "Opcional para reportes de costo por equipo.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Abra Historial.",
      "Cree registro al finalizar un mantenimiento.",
      "Vincule la alerta si el sistema lo permite.",
      "Guarde y verifique que la alerta desapareció."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Historial de mantenimientos"
      },
      steps: [
        "Abra Historial.",
        "Cree registro al finalizar un mantenimiento.",
        "Vincule la alerta si el sistema lo permite.",
        "Guarde y verifique que la alerta desapareció."
      ]
    },
    validations: [
      "Código o placa únicos según tipo de equipo.",
      "Categoría y estado coherentes con reportes."
    ],
    expectedResult: [
      "Registro visible en bitácora, mantenimiento y reportes."
    ],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "maq-mantenimiento-alertas",
      "maq-reportes"
    ],
    observations: [
      "Operaciones de taller, logística o mina con equipo propio registrado."
    ],
    screenshots: [
      SHOTS.listado,
      SHOTS.formularioCompleto
    ],
    roles: "Operaciones de taller, logística o mina con equipo propio registrado."
  },

  {
    id: "maq-reportes",
    name: "Reportes de maquinaria",
    type: "report",
    route: "/machinery/reports",
    locationPath: "Maquinarias → Reportes",
    generalDescription: "Consolidados de uso, disponibilidad, costos de mantenimiento y eventos por período. Exporte para jefatura de operaciones o control de flota.",
    prerequisites: [
      "Módulo Maquinarias habilitado en Administrador → Habilitación de módulos.",
      "Usuario administrador o limitado con acceso al módulo maquinaria."
    ],
    interfaceElements: [
      {
        name: "Rango de fechas",
        controlType: "Fecha",
        required: true,
        editable: true,
        automatic: false,
        description: "Período del análisis.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Filtro por equipo o categoría",
        controlType: "Select",
        required: false,
        editable: true,
        automatic: false,
        description: "Acota el consolidado.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Exportar",
        controlType: "Botón",
        required: false,
        editable: true,
        automatic: false,
        description: "Descarga según opciones de pantalla.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Seleccione período y filtros.",
      "Genere el reporte y espere la carga.",
      "Revise totales y exporte si necesita Excel o PDF."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Reportes de maquinaria"
      },
      steps: [
        "Seleccione período y filtros.",
        "Genere el reporte y espere la carga.",
        "Revise totales y exporte si necesita Excel o PDF."
      ]
    },
    validations: [
      "Períodos muy amplios pueden tardar; acote por mes si es lento."
    ],
    expectedResult: [
      "Cuadros y tablas coherentes con eventos y mantenimientos registrados."
    ],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "maq-eventos",
      "maq-mantenimiento-historial"
    ],
    observations: [
      "Operaciones de taller, logística o mina con equipo propio registrado."
    ],
    screenshots: [
      SHOTS.listado,
      SHOTS.formularioCompleto
    ],
    roles: "Operaciones de taller, logística o mina con equipo propio registrado."
  }
].map((p) => functionDoc(p));
